import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MessageCircle, ChevronUp } from 'lucide-react';
import SignalField from '../components/SignalField';
import SignalNavigation from '../components/SignalNavigation';
import WhatsAppButton from '../components/WhatsAppButton';
import ExperienceSection from '../components/sections/ExperienceSection';
import ServicesSection from '../components/sections/ServicesSection';
import ProjectsSection from '../components/sections/ProjectsSection';
import SkillsSection from '../components/sections/SkillsSection';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import ContactSection from '../components/sections/ContactSection';
import Footer from '../components/sections/Footer';
import { personalInfo } from '../mock';

const SignalPortfolio = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* SEO Meta Tags & Schema */}
      <Helmet>
        <html lang="en" />
        <title>Maheen Kehkasha | Digital Marketer & Social Media Manager in Bengaluru</title>
        <meta 
          name="description" 
          content="Digital marketer in Bengaluru blending SEO, paid social, and content into systems that convert attention into revenue." 
        />
        <meta 
          name="keywords" 
          content="digital marketer Bengaluru, social media manager, SEO specialist, Meta ads expert, MERN developer, freelance digital marketing" 
        />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Maheen Kehkasha | Digital Marketer & Social Media Manager in Bengaluru" />
        <meta property="og:description" content="Digital marketer in Bengaluru blending SEO, paid social, and content into systems that convert attention into revenue." />
        <meta property="og:url" content="https://maheenkehkasha.netlify.app" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Maheen Kehkasha | Digital Marketer & Social Media Manager" />
        <meta name="twitter:description" content="Digital marketer in Bengaluru blending SEO, paid social, and content into systems that convert attention into revenue." />
        
        {/* JSON-LD Person Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Maheen Kehkasha",
            "jobTitle": "Digital Marketer & Social Media Manager",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Bengaluru",
              "addressCountry": "India"
            },
            "email": "connectmaheenk@gmail.com",
            "telephone": "+917899275871",
            "url": "https://maheenkehkasha.netlify.app"
          })}
        </script>
        
        {/* JSON-LD ProfessionalService Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Maheen Kehkasha Digital Marketing",
            "serviceType": [
              "SEO Services", 
              "Social Media Management", 
              "Meta Advertising", 
              "Website Development", 
              "Digital Marketing Consulting"
            ],
            "provider": {
              "@type": "Person",
              "name": "Maheen Kehkasha"
            },
            "areaServed": "Bengaluru, India"
          })}
        </script>
      </Helmet>

      {/* Navigation */}
      <SignalNavigation onContactClick={() => scrollToSection('contact')} />

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 z-40 w-12 h-12 bg-[#7C3AED] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} className="text-white" />
        </button>
      )}

      <div className="min-h-screen bg-[#0E0B1F]">
        {/* HERO SECTION */}
        <section id="home" className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
          {/* Signal Field Background Animation */}
          <SignalField />
          
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <h1 className="gradient-text mb-6 fade-up" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>
              {personalInfo.name}
            </h1>
            
            <p className="text-2xl md:text-3xl font-semibold text-[#EDE9FE] mb-4 fade-up" style={{ animationDelay: '0.1s' }}>
              {personalInfo.tagline}
            </p>
            
            <p className="text-lg md:text-xl text-[#9D97B5] mb-10 max-w-2xl mx-auto leading-relaxed fade-up" style={{ animationDelay: '0.2s' }}>
              {personalInfo.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 fade-up" style={{ animationDelay: '0.3s' }}>
              <button
                onClick={() => scrollToSection('projects')}
                className="btn-primary flex items-center gap-2"
              >
                See my work <ArrowRight size={18} />
              </button>
              
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-accent"
              >
                Let's talk
              </button>
            </div>
            
            {/* Social Links */}
            <div className="flex gap-6 justify-center fade-up" style={{ animationDelay: '0.4s' }}>
              <a
                href="https://linkedin.com/in/maheen-kehkasha-8a6a9a245"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9D97B5] hover:text-[#7C3AED] transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a
                href="https://instagram.com/maheenkehkasha"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9D97B5] hover:text-[#7C3AED] transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href={personalInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9D97B5] hover:text-[#22D3A5] transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={24} />
              </a>
            </div>
          </div>
        </section>

        {/* ABOUT SECTION */}
        <section id="about" className="py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-center mb-12">More than marketing — measurable growth</h2>
            
            <p className="text-lg text-[#9D97B5] text-center mb-12 max-w-3xl mx-auto leading-relaxed">
              I don't just run campaigns — I build the systems behind them. From search visibility to paid social to the website itself, I connect every part of the funnel around one goal: turning visitors into customers. Strategy, execution, and the technical setup to back it — handled by one person who sees the whole picture.
            </p>
            
            {/* Feature Tags */}
            <div className="flex flex-wrap gap-4 justify-center">
              <div className="px-6 py-3 rounded-full bg-[#1A1430] border border-[#7C3AED]/20 text-[#EDE9FE]">
                <span className="text-[#9D97B5]">Leads with</span> — Digital Marketing
              </div>
              <div className="px-6 py-3 rounded-full bg-[#1A1430] border border-[#7C3AED]/20 text-[#EDE9FE]">
                <span className="text-[#9D97B5]">Pairs with</span> — Social Media Management
              </div>
              <div className="px-6 py-3 rounded-full bg-[#1A1430] border border-[#7C3AED]/20 text-[#EDE9FE]">
                <span className="text-[#9D97B5]">Backed by</span> — Web Development
              </div>
            </div>
          </div>
        </section>

        {/* ALL OTHER SECTIONS - Imported Components */}
        <ExperienceSection />
        <ServicesSection />
        <ProjectsSection />
        <SkillsSection />
        <TestimonialsSection />
        <ContactSection />
        <Footer />
      </div>
    </>
  );
};

export default SignalPortfolio;
